import { IntercomCustomAttributeKey } from 'constants/IntercomCustomAttributeKey';
import { useShopData } from 'hooks/useShopData';
import { useUserData } from 'hooks/useUserData';
import { User } from 'models/User';
import { useEffect, useRef } from 'react';
import { useIntercom } from 'react-use-intercom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { selectPackageProviders } from 'redux/slices/package-providers/package-providers.selector';
import { selectShopifyCurrentSubscription } from 'redux/slices/shopify-app-pricing/shopify-app-pricing.selector';
import { selectThirdPartyFulfillmentSettings } from 'redux/slices/third-party-fulfillment-settings/third-party-fulfillment-settings.selector';
import { selectIntercominitialised, setInitialised as setIntercomInitialised } from '../../redux/slices/intercom-slice';
import { useUpdateIntercomFields } from './useUpdateIntercomFields';

const useBootstrapIntercomData = () => {
    const { handleDataToIntercom } = useUpdateIntercomFields();
    const { boot } = useIntercom();
    const ecomSettings = useAppSelector((state) => state.settings?.settings);
    const currentSubscription = useAppSelector(selectShopifyCurrentSubscription);
    const thirdPartyFulfillmentSettings = useAppSelector(selectThirdPartyFulfillmentSettings);
    const providers = useAppSelector(selectPackageProviders);
    const intercominitialised = useAppSelector(selectIntercominitialised);
    const { data: shopData } = useShopData();
    const initialised = useRef<boolean>(null);
    const dispatch = useAppDispatch();
    const { data: userData } = useUserData();

    const bootIntercom = (userData: User) => {
        boot({
            name: userData.name,
            email: userData.email,
            userId: userData.id.toString(),
            customAttributes: {
                [IntercomCustomAttributeKey.AppName]: import.meta.env.VITE_APP_APP_NAME,
                [IntercomCustomAttributeKey.StoreDomain]: shopify?.config?.shop,
                [IntercomCustomAttributeKey.Uninstalled]: false,
            },
        });
    };

    useEffect(() => {
        if (!userData) {
            return;
        }
        bootIntercom(userData);
        dispatch(setIntercomInitialised(true));
    }, [userData]);

    useEffect(() => {
        if (
            initialised ||
            !ecomSettings ||
            !currentSubscription ||
            !shopData ||
            !thirdPartyFulfillmentSettings ||
            !providers?.length ||
            !intercominitialised
        )
            return;
        initialised.current = true;
        handleDataToIntercom(ecomSettings, currentSubscription, shopData, thirdPartyFulfillmentSettings, providers);
    });
};

export default useBootstrapIntercomData;
